import { getQualityEvaluationStatus } from '~/utils/documents'
// If the backend stays in "cancelling" beyond this threshold it means a worker
// was killed mid-execution and the in-flight counter leaked — force cancelled.
export const CANCELLING_TIMEOUT_MS = 60_000
export const QUALITY_EVALUATION_COMPLETED_EVENT = 'quality-evaluation-completed'
export const QUALITY_EVALUATION_PROGRESS_EVENT = 'quality-evaluation-progress'
export const QUALITY_EVALUATION_FAILED_EVENT = 'quality-evaluation-failed'
export const QUALITY_EVALUATION_CANCELLED_EVENT = 'quality-evaluation-cancelled'

export class QualityEvaluationPollingService {
  constructor() {
    this.activePollings = new Map()
    this.errorCounts = new Map()
    this.cancellingStartedAt = new Map()
    this.pollingInterval = 2000
    this.maxConsecutiveErrors = 3
    this.toast = null
  }

  init(toastInstance) {
    this.toast = toastInstance
    this.loadAndStartPolling()
  }

  loadAndStartPolling() {
    if (typeof window === 'undefined') return

    try {
      const qualityKeys = Object.keys(localStorage).filter((key) =>
        key.startsWith('document-editor-quality-')
      )

      for (const key of qualityKeys) {
        try {
          const qualityInfo = JSON.parse(localStorage.getItem(key))

          if (
            qualityInfo &&
            ['completed', 'failed', 'cancelled'].includes(qualityInfo.qe_status)
          ) {
            localStorage.removeItem(key)
            continue
          }

          if (
            qualityInfo &&
            (qualityInfo.qe_status === 'processing' ||
              qualityInfo.qe_status === 'cancelling') &&
            qualityInfo.docId &&
            qualityInfo.filename
          ) {
            this.startPolling(qualityInfo.docId, qualityInfo.filename)
          }
        } catch {
          localStorage.removeItem(key)
        }
      }
    } catch (error) {
      console.error('Error loading quality evaluations:', error)
    }
  }

  // Returns true once "cancelling" has been reported for longer than
  // CANCELLING_TIMEOUT_MS, meaning a worker was killed mid-execution and the
  // in-flight counter leaked. Also clears the tracked start time whenever the
  // backend isn't (or is no longer) reporting "cancelling".
  _isCancellingTimedOut(docId, backendStatus) {
    if (backendStatus !== 'cancelling') {
      this.cancellingStartedAt.delete(docId)
      return false
    }

    if (!this.cancellingStartedAt.has(docId)) {
      this.cancellingStartedAt.set(docId, Date.now())
      return false
    }

    return (
      Date.now() - this.cancellingStartedAt.get(docId) > CANCELLING_TIMEOUT_MS
    )
  }

  _syncProgressStorage(docId, backendStatus, progress) {
    const storageKey = `document-editor-quality-${docId}`
    const existingInfo = localStorage.getItem(storageKey)

    if (!existingInfo) {
      return
    }

    const qualityInfo = JSON.parse(existingInfo)
    qualityInfo.progress = progress

    // Don't let "processing" from an in-flight batch overwrite the
    // "cancelling" state the user already triggered.
    const keepCancellingState =
      qualityInfo.qe_status === 'cancelling' && backendStatus === 'processing'

    if (!keepCancellingState) {
      qualityInfo.qe_status = backendStatus
    }

    localStorage.setItem(storageKey, JSON.stringify(qualityInfo))
    window.dispatchEvent(
      new CustomEvent(QUALITY_EVALUATION_PROGRESS_EVENT, {
        detail: { docId, progress },
      })
    )
  }

  _handleCompleted(docId, filename) {
    this.stopPolling(docId)
    localStorage.removeItem(`document-editor-quality-${docId}`)

    this.toast?.add({
      title: this.getCompletionMessage(filename),
      color: 'primary',
      icon: 'i-heroicons-check-circle',
      actions: [
        {
          label: this.getViewDocumentLabel(),
          color: 'primary',
          variant: 'solid',
          onClick: (e) => {
            e?.stopPropagation()
            this.navigateToDocument(docId)
          },
        },
      ],
    })

    window.dispatchEvent(
      new CustomEvent(QUALITY_EVALUATION_COMPLETED_EVENT, {
        detail: { docId, filename },
      })
    )
  }

  _handleFailed(docId, filename) {
    this.stopPolling(docId)
    localStorage.removeItem(`document-editor-quality-${docId}`)

    this.toast?.add({
      title: this.getErrorMessage(filename),
      color: 'error',
      icon: 'i-heroicons-x-circle',
    })

    window.dispatchEvent(
      new CustomEvent(QUALITY_EVALUATION_FAILED_EVENT, {
        detail: { docId, filename, qe_status: 'failed' },
      })
    )
  }

  startPolling(docId, filename) {
    this.stopPolling(docId)
    this.errorCounts.delete(docId)

    const intervalId = setInterval(async () => {
      try {
        const response = await getQualityEvaluationStatus(docId)
        this.errorCounts.set(docId, 0)

        const backendStatus = response?.qe_status
        let progress = 0
        if (response?.progress !== undefined && response.progress !== null) {
          progress = Math.min(100, Math.max(0, response.progress))
        }

        if (this._isCancellingTimedOut(docId, backendStatus)) {
          this._handleCancelled(docId, filename)
          return
        }

        // A null/undefined qe_status means the backend has no evaluation
        // recorded for this document at all — nothing is running, and never
        // will be. Without this, a stale "processing" entry (e.g. one whose
        // start request failed to reach a runnable state) polls forever,
        // since none of the branches below ever match a falsy status.
        if (!backendStatus) {
          this._handleCancelled(docId, filename)
          return
        }

        this._syncProgressStorage(docId, backendStatus, progress)

        const isCompleted =
          progress >= 100 ||
          backendStatus === 'completed' ||
          backendStatus === 'finished'

        if (isCompleted) {
          this._handleCompleted(docId, filename)
        } else if (backendStatus === 'cancelled') {
          this._handleCancelled(docId, filename)
        } else if (backendStatus === 'failed' || backendStatus === 'error') {
          this._handleFailed(docId, filename)
        }
      } catch (error) {
        const currentErrors = (this.errorCounts.get(docId) || 0) + 1
        this.errorCounts.set(docId, currentErrors)

        if (currentErrors >= this.maxConsecutiveErrors) {
          this.stopPolling(docId)
          localStorage.removeItem(`document-editor-quality-${docId}`)

          this.toast?.add({
            title: this.getErrorMessage(filename),
            color: 'warning',
            icon: 'i-heroicons-exclamation-triangle',
          })

          window.dispatchEvent(
            new CustomEvent(QUALITY_EVALUATION_FAILED_EVENT, {
              detail: { docId, filename },
            })
          )
          return
        }

        console.error(
          `Error polling quality evaluation status for ${docId}:`,
          error
        )
      }
    }, this.pollingInterval)

    this.activePollings.set(docId, intervalId)
  }

  _handleCancelled(docId, filename) {
    this.stopPolling(docId)
    localStorage.removeItem(`document-editor-quality-${docId}`)

    window.dispatchEvent(
      new CustomEvent(QUALITY_EVALUATION_CANCELLED_EVENT, {
        detail: { docId, filename, qe_status: 'cancelled' },
      })
    )
  }

  stopPolling(docId) {
    const intervalId = this.activePollings.get(docId)
    if (intervalId) {
      clearInterval(intervalId)
      this.activePollings.delete(docId)
    }
    this.errorCounts.delete(docId)
    this.cancellingStartedAt.delete(docId)
  }

  stopAll() {
    for (const [, intervalId] of this.activePollings) {
      clearInterval(intervalId)
    }
    this.activePollings.clear()
    this.errorCounts.clear()
    this.cancellingStartedAt.clear()
  }

  getCompletionMessage(filename) {
    try {
      return this.nuxtApp.$i18n.t(
        'documents_editor.evaluation_completed_notification',
        { filename }
      )
    } catch {
      return `Document "${filename}" is ready`
    }
  }

  getErrorMessage(filename) {
    try {
      return this.nuxtApp.$i18n.t(
        'documents_editor.evaluation_failed_notification',
        { filename }
      )
    } catch {
      return `Quality evaluation failed for "${filename}"`
    }
  }

  getViewDocumentLabel() {
    try {
      return this.nuxtApp.$i18n.t('documents_editor.view_document')
    } catch {
      return 'View Document'
    }
  }

  navigateToDocument(docId) {
    if (typeof window !== 'undefined') {
      window.location.href = `/document-editor?doc_id=${docId}`
    }
  }
}

const qualityEvaluationPolling = new QualityEvaluationPollingService()

export default defineNuxtPlugin((nuxtApp) => {
  const toast = useToast()
  const router = useRouter()

  qualityEvaluationPolling.nuxtApp = nuxtApp
  qualityEvaluationPolling.router = router
  qualityEvaluationPolling.init(toast)

  nuxtApp.provide('qualityEvaluationPolling', qualityEvaluationPolling)
})
